import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { useIsDesktop } from './useMediaQuery';
import './styles/toast.css';

type Kind = 'success' | 'error' | 'info' | 'warning';
interface ToastItem {
  id: number;
  message: ReactNode;
  kind: Kind;
}

const Ctx = createContext<{ show: (message: ReactNode, kind?: Kind) => void } | null>(null);

const TOAST_DURATION_MS = 4000;

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const isDesktop = useIsDesktop();

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const show = useCallback(
    (message: ReactNode, kind: Kind = 'info') => {
      const id = Date.now() + Math.random();
      setToasts((t) => [...t, { id, message, kind }]);
      window.setTimeout(() => dismiss(id), TOAST_DURATION_MS);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className={cx('cev-toast-stack', isDesktop ? 'cev-toast-stack--desktop' : 'cev-toast-stack--mobile')}
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  return (
    <div data-testid={`toast-${toast.kind}`} className={cx('cev-toast', `cev-toast--${toast.kind}`)}>
      <span className="cev-toast__message">{toast.message}</span>
      <button type="button" className="cev-toast__dismiss" aria-label="Dismiss" onClick={onDismiss}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast must be used inside <ToastProvider>');
  return v.show;
}
